import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { reserveAllMaterials, getMaterialReservations, type StandardCostPlan } from '@/api/productionOrder.api';
import { money } from '@/pages/masters/routing/stageCost';
import { useToast } from '@/contexts/ToastContext';
import ItemTypeBadge from './ItemTypeBadge';

export interface OrderConfirmationSummaryData {
  prd_id?: string;
  prd_number?: string;
  status?: string;
  item_name?: string;
  item_code?: string;
  warehouse_name?: string;
  planned_qty?: number;
  has_shortage?: boolean;
  reservations?: Array<{
    id?: string;
    itemId?: string;
    itemName?: string;
    itemCode?: string;
    itemType?: string | null;
    unit?: string;
    requiredQty?: number;
    availableStock?: number;
    stockAvailable?: number;
    reservedQty?: number;
    issuedQty?: number;
    shortage?: number;
    status?: string;
  }>;
  materials?: Array<{
    id?: string;
    itemId?: string;
    itemName?: string;
    itemCode?: string;
    itemType?: string | null;
    unit?: string;
    requiredQty?: number;
    availableStock?: number;
    stockAvailable?: number;
    reservedQty?: number;
    issuedQty?: number;
    shortage?: number;
    status?: string;
  }>;
  reservation_summary?: {
    reservedCount?: number;
    partialCount?: number;
    shortageCount?: number;
    message?: string;
  };
  work_orders?: Array<{
    id?: string;
    wo_number?: string;
    stage_number?: number;
    stage_name?: string;
    is_locked?: boolean;
    planned_time_minutes?: number;
  }>;
  standard_cost?: {
    material?: number;
    labour?: number;
    overhead?: number;
    total?: number;
    per_unit?: number;
  };
  /** The standard cost plan the figures come from (routing stages at work center rates) */
  cost_plan?: StandardCostPlan | null;
  data?: any;
}

interface OrderConfirmationSummaryModalProps {
  summary: OrderConfirmationSummaryData | null;
  onClose: () => void;
  onViewOrderDetails?: (orderId: string) => void;
}

export default function OrderConfirmationSummaryModal({
  summary,
  onClose,
  onViewOrderDetails,
}: OrderConfirmationSummaryModalProps) {
  const navigate = useNavigate();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState<'materials' | 'work_orders' | 'costing'>('materials');
  const [reserving, setReserving] = useState(false);

  // Local state for material reservations so that clicking "Reserve All" inside modal can refresh live
  const [reservations, setReservations] = useState<any[]>([]);

  useEffect(() => {
    if (!summary) return;
    const initialList = summary.reservations?.length
      ? summary.reservations
      : summary.materials?.length
      ? summary.materials
      : [];
    setReservations(initialList);

    // If reservations has no reservedQty populated, fetch the actual reservations for the order
    const poId = summary.prd_id || summary.data?.id;
    // Also refetch when item types are missing (summary came from an older/partial payload)
    const missingReserved = initialList.every((r: any) => r.reservedQty === undefined && r.reserved_qty === undefined);
    const missingTypes = initialList.some((r: any) => !r.itemType && !r.item_type);
    if (poId && (!initialList.length || missingReserved || missingTypes)) {
      getMaterialReservations(poId)
        .then((res: any) => {
          if (res?.data && Array.isArray(res.data) && res.data.length > 0) {
            setReservations(res.data);
          }
        })
        .catch(() => {});
    }
  }, [summary]);

  if (!summary) return null;

  const poId = summary.prd_id || summary.data?.id || '';
  const poNumber = summary.prd_number || summary.data?.po_number || 'PRD-ORDER';
  const itemName = summary.item_name || summary.data?.item_name || summary.data?.product_name || 'Finished Good';
  const itemCode = summary.item_code || summary.data?.item_code || '';
  const warehouseName = summary.warehouse_name || summary.data?.warehouse_name || 'Main Warehouse';
  const plannedQty = Number(summary.planned_qty || summary.data?.planned_qty || 1);

  const workOrders = summary.work_orders || [];
  const stdCost = summary.standard_cost || {
    material: Number(summary.data?.standard_material_cost || 0),
    labour: Number(summary.data?.standard_labour_cost || 0),
    overhead: Number(summary.data?.standard_overhead_cost || 0),
    total: Number(summary.data?.standard_total_cost || 0),
    per_unit: plannedQty > 0 ? Math.round(Number(summary.data?.standard_total_cost || 0) / plannedQty) : 0,
  };

  const handleReserveAll = async () => {
    if (!poId) return;
    setReserving(true);
    try {
      const res: any = await reserveAllMaterials(poId);
      if (res && res.success) {
        if (Array.isArray(res.data)) {
          setReservations(res.data);
        }
        if (res.shortageCount > 0 || res.partialCount > 0) {
          toast.warning(res.message || 'Some items have shortage in warehouse stock ⚠️');
        } else {
          toast.success(res.message || 'Reserved all materials successfully!');
        }
      } else {
        toast.error(res?.message || 'Failed to reserve materials');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to reserve materials');
    } finally {
      setReserving(false);
    }
  };

  const shortageItems = reservations.filter((m) => {
    const req = Number(m.requiredQty ?? m.required_qty ?? 0);
    const stock = Math.round(Number(m.availableStock ?? m.stockAvailable ?? m.stock_available ?? 0) * 100) / 100;
    const reserved = Number(m.reservedQty ?? m.reserved_qty ?? 0);
    return m.status === 'SHORTAGE' || stock < req || (reserved < req && stock === 0);
  });

  const fullyReservedCount = reservations.filter((m) => {
    const req = Number(m.requiredQty ?? m.required_qty ?? 0);
    const reserved = Number(m.reservedQty ?? m.reserved_qty ?? 0);
    return (m.status === 'RESERVED' || (reserved >= req && req > 0)) && reserved > 0;
  }).length;

  const handleGoToOrder = () => {
    onClose();
    if (onViewOrderDetails && poId) {
      onViewOrderDetails(poId);
    } else if (poId) {
      navigate(`/manufacturing/production-orders/${poId}`);
    } else {
      navigate('/manufacturing/production-orders');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] flex flex-col shadow-2xl border border-slate-200 overflow-hidden">
        
        {/* ── Modal Header ── */}
        <div className="px-6 py-4.5 bg-gradient-to-r from-slate-50 to-indigo-50/30 border-b border-slate-200 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 border border-emerald-300 flex items-center justify-center text-emerald-600 shadow-2xs">
              <i className="ri-checkbox-circle-fill text-2xl" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-slate-800 tracking-tight">Order Confirmed & Reserved</h2>
                <span className="px-2.5 py-0.5 rounded-md text-xs font-mono font-semibold bg-white text-slate-800 border border-slate-300 shadow-2xs">
                  {poNumber}
                </span>
                <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                  PLANNED
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Work orders generated, standard costing initialized, and materials reserved in warehouse.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition-colors cursor-pointer"
            title="Close"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* ── Modal Content ── */}
        <div className="p-6 overflow-y-auto space-y-4 text-sm flex-1">

          {/* 1. Summary Strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/80">
              <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider block">Finished Good</span>
              <p className="text-sm font-semibold text-slate-800 mt-1 truncate" title={itemName}>
                {itemName}
              </p>
              {itemCode && <p className="text-[11px] text-slate-400 font-mono">{itemCode}</p>}
            </div>

            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/80">
              <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider block">Planned Output</span>
              <p className="text-base font-bold text-slate-900 mt-1">
                {plannedQty} <span className="text-xs font-normal text-slate-500">Units</span>
              </p>
            </div>

            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/80">
              <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider block">Target Warehouse</span>
              <p className="text-sm font-semibold text-slate-800 mt-1 truncate" title={warehouseName}>
                {warehouseName}
              </p>
            </div>

            <div className="p-3 rounded-xl bg-indigo-50/70 border border-indigo-100">
              <span className="text-[11px] font-medium text-indigo-600 uppercase tracking-wider block">Total Standard Cost</span>
              <p className="text-base font-bold text-indigo-700 mt-1">
                {money(Number(stdCost.total || 0))}
              </p>
              <p className="text-[10px] text-indigo-500 font-medium">
                {money(Number(stdCost.per_unit || 0))}/unit
              </p>
            </div>
          </div>

          {/* 2. Navigation Tabs */}
          <div className="flex items-center gap-1 border-b border-slate-200 pt-1">
            <button
              type="button"
              onClick={() => setActiveTab('materials')}
              className={`pb-2.5 px-3 text-xs font-semibold cursor-pointer border-b-2 flex items-center gap-1.5 transition-colors ${
                activeTab === 'materials'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <i className="ri-archive-line text-sm" />
              <span>Material Reservations ({reservations.length})</span>
              {shortageItems.length > 0 ? (
                <span className="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">
                  {shortageItems.length} short
                </span>
              ) : (
                <span className="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">
                  ✓
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('work_orders')}
              className={`pb-2.5 px-3 text-xs font-semibold cursor-pointer border-b-2 flex items-center gap-1.5 transition-colors ${
                activeTab === 'work_orders'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <i className="ri-git-merge-line text-sm" />
              <span>Work Orders ({workOrders.length})</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('costing')}
              className={`pb-2.5 px-3 text-xs font-semibold cursor-pointer border-b-2 flex items-center gap-1.5 transition-colors ${
                activeTab === 'costing'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <i className="ri-money-rupee-circle-line text-sm" />
              <span>Standard Costing</span>
            </button>
          </div>

          {/* 3. TAB CONTENT */}
          {activeTab === 'materials' && (
            <div className="space-y-3">
              {/* Material Reservations Header Bar */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-800 uppercase tracking-wide">
                    Allocated Components
                  </span>
                  <span className="text-[11px] text-slate-500">
                    ({fullyReservedCount} of {reservations.length} fully reserved)
                  </span>
                </div>

                <button
                  type="button"
                  onClick={handleReserveAll}
                  disabled={reserving || !poId || reservations.length === 0}
                  className="h-8 px-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold shadow-xs flex items-center gap-1.5 transition-all cursor-pointer disabled:opacity-50"
                  title="Reserve available stock for all components"
                >
                  {reserving ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-sm" />
                      <span>Reserving...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5 stroke-[2.5]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="3" x2="12" y2="15" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="5" y1="20" x2="19" y2="20" />
                      </svg>
                      <span>Reserve All</span>
                    </>
                  )}
                </button>
              </div>

              {/* Material Reservations Table */}
              <div className="rounded-xl border border-slate-200 overflow-hidden shadow-2xs">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50/80 border-b border-slate-200/90 text-slate-500 font-semibold tracking-wide">
                      <th className="text-left px-4 py-2.5">Item Name</th>
                      <th className="text-right px-4 py-2.5">Required Qty</th>
                      <th className="text-center px-4 py-2.5">Available Stock</th>
                      <th className="text-right px-4 py-2.5 text-indigo-900 bg-indigo-50/40">Reserved Qty</th>
                      <th className="text-center px-4 py-2.5">Pending Qty</th>
                      <th className="text-left px-3 py-2.5">Unit</th>
                      <th className="text-center px-4 py-2.5">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {reservations.length > 0 ? (
                      reservations.map((m, idx) => {
                        const req = Number(m.requiredQty ?? m.required_qty ?? 0);
                        const reserved = Number(m.reservedQty ?? m.reserved_qty ?? 0);
                        const stock = Math.round(Number(m.availableStock ?? m.stockAvailable ?? m.stock_available ?? 0) * 100) / 100;
                        const pending = Math.max(0, req - reserved);
                        const isReserved = (m.status === 'RESERVED' || (reserved >= req && req > 0)) && reserved > 0;
                        const isPartial = (m.status === 'PARTIAL' || (reserved > 0 && reserved < req)) && !isReserved;

                        return (
                          <tr key={idx} className="hover:bg-slate-50/60 transition-colors">
                            <td className="px-4 py-2.5">
                              <div className="font-semibold text-slate-800">{m.itemName || m.item_name}</div>
                              <div className="flex items-center gap-1.5 flex-wrap mt-1">
                                <ItemTypeBadge type={m.itemType || m.item_type} />
                                {(m.itemCode || m.item_code) && (
                                  <span className="text-[10px] font-mono text-slate-400">{m.itemCode || m.item_code}</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-right font-medium text-slate-700">
                              {req.toFixed(2)}
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              {stock > 0 ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  <i className="ri-check-line text-xs" />
                                  <span>{stock.toFixed(2)} {m.unit || 'Unit'}</span>
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-rose-50 text-rose-700 border border-rose-200">
                                  <i className="ri-close-circle-line text-xs" />
                                  <span>0.00 {m.unit || 'Unit'} (Out of Stock)</span>
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-right font-bold text-indigo-700 bg-indigo-50/40">
                              {reserved.toFixed(2)}
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              {pending > 0 ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                  <i className="ri-time-line text-xs" />
                                  <span>{pending.toFixed(2)}</span>
                                </span>
                              ) : (
                                <span className="text-slate-400 font-mono">0.00</span>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-slate-500 font-medium">{m.unit || 'Unit'}</td>
                            <td className="px-4 py-2.5 text-center">
                              {isReserved ? (
                                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 inline-flex items-center gap-1">
                                  <i className="ri-checkbox-circle-fill text-xs" /> RESERVED
                                </span>
                              ) : isPartial ? (
                                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300 inline-flex items-center gap-1">
                                  <i className="ri-error-warning-fill text-xs" /> PARTIAL
                                </span>
                              ) : (
                                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-300 inline-flex items-center gap-1">
                                  <i className="ri-close-circle-line text-xs text-slate-500" /> UNRESERVED
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={7} className="px-4 py-6 text-center text-slate-400 italic">
                          No material components required for this item.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Shortage Alert Callout */}
              {shortageItems.length > 0 && (
                <div className="p-3 rounded-xl bg-amber-50/80 border border-amber-200 flex items-start gap-2.5 text-xs text-amber-800">
                  <i className="ri-alert-line text-amber-600 text-base shrink-0 mt-0.5" />
                  <div>
                    <strong className="font-semibold block">Inventory Shortage Detected</strong>
                    <span>
                      {shortageItems.length} material(s) have insufficient stock in {warehouseName}. You can run MRP or create Purchase Orders to fulfill shortages before material issue.
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'work_orders' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Routing Work Orders ({workOrders.length})
                </span>
                <span className="text-[11px] text-emerald-700 font-medium flex items-center gap-1">
                  <i className="ri-lock-unlock-line text-xs" /> Stage 1 ready to execute
                </span>
              </div>

              {workOrders.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {workOrders.map((wo, idx) => {
                    const isStage1 = Number(wo.stage_number || idx + 1) === 1;
                    return (
                      <div
                        key={idx}
                        className="p-2.5 rounded-lg border border-slate-200/80 bg-slate-50/60 flex items-center justify-between text-xs"
                      >
                        <div className="min-w-0 pr-2">
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono font-bold text-slate-800">{wo.wo_number}</span>
                            <span className="text-[10px] text-slate-400 font-medium">Stage {wo.stage_number || idx + 1}</span>
                          </div>
                          <p className="text-slate-600 font-medium text-[11px] truncate mt-0.5">{wo.stage_name}</p>
                        </div>
                        <div className="shrink-0 text-right">
                          {isStage1 ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                              <i className="ri-play-circle-line text-xs" /> Ready
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500 border border-slate-200 inline-flex items-center gap-1">
                              <i className="ri-lock-line text-[10px]" /> Locked
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-slate-400 italic py-4 text-center">
                  Single-stage production — no multi-stage work orders generated.
                </p>
              )}
            </div>
          )}

          {activeTab === 'costing' && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="ri-money-rupee-circle-line text-indigo-600 text-base" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">Standard Costing Plan</h3>
                </div>
                <span className="text-[11px] text-slate-400 font-medium">
                  {summary.cost_plan
                    ? `Routing stages at work center rates · ${summary.cost_plan.overheadPct}% overhead · ${money(summary.cost_plan.totals.perUnit)}/unit`
                    : 'Standard cost'}
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100 text-center">
                  <span className="text-[11px] text-slate-400 font-medium block">Raw Materials</span>
                  <span className="text-sm font-bold text-slate-800 mt-0.5 block">
                    {money(Number(stdCost.material || 0))}
                  </span>
                </div>
                <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100 text-center">
                  <span className="text-[11px] text-slate-400 font-medium block">Direct Labour</span>
                  <span className="text-sm font-bold text-slate-800 mt-0.5 block">
                    {money(Number(stdCost.labour || 0))}
                  </span>
                </div>
                <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100 text-center">
                  <span className="text-[11px] text-slate-400 font-medium block">Factory Overhead</span>
                  <span className="text-sm font-bold text-slate-800 mt-0.5 block">
                    {money(Number(stdCost.overhead || 0))}
                  </span>
                </div>
                <div className="p-2.5 rounded-lg bg-indigo-50/70 border border-indigo-100 text-center">
                  <span className="text-[11px] text-indigo-600 font-semibold block">Total Cost</span>
                  <span className="text-sm font-bold text-indigo-900 mt-0.5 block">
                    {money(Number(stdCost.total || 0))}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* 4. System Status Strip */}
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/80 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
            <span className="flex items-center gap-1.5 font-medium text-slate-700">
              <i className="ri-check-line text-emerald-600" /> Material reservations linked
            </span>
            <span className="flex items-center gap-1.5 font-medium text-slate-700">
              <i className="ri-check-line text-emerald-600" /> MRP synchronized
            </span>
            <span className="flex items-center gap-1.5 font-medium text-slate-700">
              <i className="ri-check-line text-emerald-600" /> Ready for material issue
            </span>
          </div>

        </div>

        {/* ── Modal Footer ── */}
        <div className="px-6 py-4 bg-slate-50/90 border-t border-slate-200 flex items-center justify-between gap-3 shrink-0">
          <button
            type="button"
            onClick={() => {
              onClose();
              navigate('/manufacturing/production-orders');
            }}
            className="h-9 px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold cursor-pointer transition-colors shadow-2xs"
          >
            Back to Orders List
          </button>
          
          <button
            type="button"
            onClick={handleGoToOrder}
            className="h-9 px-5 rounded-xl bg-[#4f46e5] hover:bg-indigo-700 text-white text-xs font-semibold inline-flex items-center gap-2 cursor-pointer shadow-xs transition-colors"
          >
            <span>View Order Details</span>
            <i className="ri-arrow-right-line" />
          </button>
        </div>

      </div>
    </div>
  );
}
